import { useMemo, useRef, useState } from 'react';

import type { LocalImageEdit } from './ImagesVideo/ImageEditor/ImageEditorUtils';
import type { RoomImageOrderOverrides } from './ImagesVideo/ImagesByRoom/utils';
import type { MediaImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  buildMediaImages,
  dedupeMediaImages,
  getMediaImageSourceKey,
  getPrimaryVideoId,
  isRemovedRoomGalleryImage,
} from './ImagesVideo/utils';
import type { RemovedRoomImageIdsByRoomKey, UploadedRoomAssignment } from './ImagesVideo/utils';
import useOfferBuilderMediaEdits from './useOfferBuilderMediaEdits';
import useOfferBuilderMediaOrder from './useOfferBuilderMediaOrder';
import useOfferBuilderMediaUpload, { type UploadFilesOptions } from './useOfferBuilderMediaUpload';
import useOfferBuilderMediaVisibility from './useOfferBuilderMediaVisibility';

interface UseOfferBuilderMediaParams {
  offerId: string;
  images?: Array<App.OfferImage>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

export interface OfferBuilderMediaController {
  removedRoomImageIdsByRoomKey: RemovedRoomImageIdsByRoomKey;
  hasUnsavedOfferChanges: boolean;
  hasUnsavedRoomImageChanges: boolean;
  hasUnsavedRoomImageTitleEdits: boolean;
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  imagePublicIdReplacements: Record<string, string>;
  offerImageOrderOverrides: Array<string>;
  savedOfferGalleryOrder: Array<string>;
  savedRoomImageOrders: RoomImageOrderOverrides;
  isUploadingImages: boolean;
  isSavingChanges: boolean;
  pendingOversizedUpload?: { file: File; filename: string };
  mediaImages: Array<MediaImage>;
  roomImagePendingDeletion?: MediaImage;
  roomImageOrderOverrides: RoomImageOrderOverrides;
  roomImageOrderResetKey: number;
  roomImagePendingEdit?: MediaImage;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
  videoId?: string;
  closeRoomImageDeleteDialog: () => void;
  confirmRemoveRoomImage: () => Promise<void>;
  deleteImage: (publicId: string, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  discardRoomImageTitleEdits: () => void;
  removeRoomImageFromEditor: (image: MediaImage) => void;
  removeImageFromRoom: (image: MediaImage, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  reorderOfferGalleryImages: (orderedPublicIds: Array<string>) => void;
  reorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => void;
  saveRoomImageChanges: (roomKey?: string, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  saveImagesVideoChanges: () => Promise<void>;
  discardChanges: (scope?: 'all' | 'offer' | 'room') => void;
  saveRoomImageTitleEdits: (roomKey: string) => Promise<void>;
  saveImageEdit: (publicId: string, edit: LocalImageEdit, persistedPublicId?: string) => Promise<void>;
  setRoomImagePendingEdit: (image?: MediaImage) => void;
  setVideoId: (videoId?: string) => void;
  toggleHiddenImage: (publicId: string) => Promise<void>;
  updateImageTitle: (roomKey: string, publicId: string, title: string) => void;
  uploadFiles: (files: Array<File>, options?: UploadFilesOptions) => Promise<'uploaded' | 'queued' | 'failed'>;
  uploadFilesToOfferGallery: (files: Array<File>) => Promise<void>;
  uploadFilesToRoom: (roomId: string, roomName: string, files: Array<File>) => Promise<void>;
  cancelOversizedUpload: () => void;
  confirmOversizedUpload: (file: File) => Promise<boolean>;
  editRoomImage: (publicId: string) => void;
}

export default function useOfferBuilderMedia({
  offerId,
  images = [],
  propertyRoomTypes,
  videos,
}: UseOfferBuilderMediaParams): OfferBuilderMediaController {
  const [deletedImageSourceKeys, setDeletedImageSourceKeys] = useState<Array<string>>([]);
  const [removedRoomImageIdsByRoomKey, setRemovedRoomImageIdsByRoomKey] = useState<RemovedRoomImageIdsByRoomKey>({});
  const [replacedImageSourceKeys, setReplacedImageSourceKeys] = useState<Array<string>>([]);
  const [localVideoId, setLocalVideoId] = useState<string | undefined>();
  const videoId = localVideoId === '' ? undefined : (localVideoId ?? getPrimaryVideoId(videos));

  const upload = useOfferBuilderMediaUpload({ offerId, propertyRoomTypes });

  const setHiddenImageOverridesRef = useRef<
    (updater: (current: Record<string, boolean>) => Record<string, boolean>) => void
  >(() => {});

  const mediaImages = useMemo(() => {
    const deletedImageSourceKeySet = new Set(deletedImageSourceKeys);
    const replacedImageSourceKeySet = new Set(replacedImageSourceKeys);

    return dedupeMediaImages(
      buildMediaImages({
        offerImages: images,
        propertyRoomTypes,
      }).concat(upload.uploadedImages),
    )
      .filter((image) => !deletedImageSourceKeySet.has(getMediaImageSourceKey(image)))
      .filter((image) => !replacedImageSourceKeySet.has(getMediaImageSourceKey(image)))
      .filter((image) => !isRemovedRoomGalleryImage(image, removedRoomImageIdsByRoomKey));
  }, [
    deletedImageSourceKeys,
    images,
    propertyRoomTypes,
    removedRoomImageIdsByRoomKey,
    replacedImageSourceKeys,
    upload.uploadedImages,
  ]);

  const edits = useOfferBuilderMediaEdits({
    offerId,
    mediaImages,
    propertyRoomTypes,
    setHiddenImageOverrides: (updater) => setHiddenImageOverridesRef.current(updater),
    replaceUploadedImage: (_sourceKey, nextImage) => {
      const nextImageSourceKey = getMediaImageSourceKey(nextImage);

      setReplacedImageSourceKeys((currentKeys) => {
        // The new image's own key must never stay blacklisted, otherwise reverting an edit
        // back to a previously-replaced publicId (e.g. the original) hides it for good.
        const nextKeys = currentKeys.filter((key) => key !== nextImageSourceKey);

        return nextKeys.includes(_sourceKey) ? nextKeys : nextKeys.concat(_sourceKey);
      });
      upload.replaceUploadedImage(nextImage.publicId, nextImage);
    },
  });

  const mediaImagesWithTitles = useMemo(
    () =>
      mediaImages.map((image) => {
        const imageSourceKey = getMediaImageSourceKey(image);
        const imageEdit = edits.imageEdits[imageSourceKey] ?? edits.imageEdits[image.publicId];

        return {
          ...image,
          title: imageEdit?.title ?? edits.savedImageTitles[imageSourceKey] ?? image.title,
        };
      }),
    [edits.imageEdits, edits.savedImageTitles, mediaImages],
  );

  const markRoomImageRemoved = (roomKey: string, publicId: string) => {
    setRemovedRoomImageIdsByRoomKey((currentRemovedImagesByRoomKey) => ({
      ...currentRemovedImagesByRoomKey,
      [roomKey]: [...new Set((currentRemovedImagesByRoomKey[roomKey] ?? []).concat(publicId))],
    }));
  };

  const markDeletedOfferImage = (publicId: string) => {
    const deletedSourceKeys = mediaImagesWithTitles
      .filter((mediaImage) => mediaImage.publicId === publicId && mediaImage.sourceType === 'offer')
      .map((mediaImage) => getMediaImageSourceKey(mediaImage));

    if (deletedSourceKeys.length === 0) {
      return;
    }

    setDeletedImageSourceKeys((currentKeys) => [...new Set(currentKeys.concat(deletedSourceKeys))]);
  };

  const order = useOfferBuilderMediaOrder({
    offerId,
    offerImages: images,
    uploadedImages: upload.uploadedImages,
    mediaImages: mediaImagesWithTitles,
    propertyRoomTypes,
    imagePublicIdReplacements: edits.imagePublicIdReplacements,
    removedRoomImageIdsByRoomKey,
  });

  const visibility = useOfferBuilderMediaVisibility({
    offerId,
    mediaImages: mediaImagesWithTitles,
    removeUploadedImage: upload.removeUploadedImage,
    markDeletedOfferImage,
    markRoomImageRemoved,
    cleanupDeletedOfferGalleryImage: (publicId, deletedImageWasPending) =>
      order.syncOrdersAfterDelete('offer', undefined, publicId, deletedImageWasPending),
    cleanupDeletedRoomGalleryImage: (roomKey, publicId, deletedImageWasPending) =>
      order.syncOrdersAfterDelete('room', roomKey, publicId, deletedImageWasPending),
    clearImageEdit: edits.clearImageEdit,
    setRoomImagePendingEdit: edits.setRoomImagePendingEdit,
  });
  setHiddenImageOverridesRef.current = visibility.setHiddenImageOverrides;

  const hiddenImagePublicIds = mediaImagesWithTitles
    .filter((image) => visibility.hiddenImageOverrides[image.publicId] ?? image.hidden)
    .map((image) => image.publicId);

  return {
    removedRoomImageIdsByRoomKey,
    hasUnsavedOfferChanges: order.hasUnsavedOfferChanges,
    hasUnsavedRoomImageChanges: order.hasUnsavedRoomImageChanges,
    hasUnsavedRoomImageTitleEdits: edits.hasUnsavedRoomImageTitleEdits,
    hiddenImagePublicIds,
    imageEdits: edits.imageEdits,
    imagePublicIdReplacements: edits.imagePublicIdReplacements,
    offerImageOrderOverrides: order.offerImageOrderOverrides,
    savedOfferGalleryOrder: order.savedOfferGalleryOrder,
    savedRoomImageOrders: order.savedRoomImageOrders,
    isUploadingImages: upload.isUploadingImages,
    isSavingChanges: order.isSavingChanges,
    pendingOversizedUpload: upload.pendingOversizedUpload,
    mediaImages: mediaImagesWithTitles,
    roomImagePendingDeletion: visibility.roomImagePendingDeletion,
    roomImageOrderOverrides: order.roomImageOrderOverrides,
    roomImageOrderResetKey: order.roomImageOrderResetKey,
    roomImagePendingEdit: edits.roomImagePendingEdit,
    uploadedRoomAssignments: upload.uploadedRoomAssignments,
    videoId,
    closeRoomImageDeleteDialog: visibility.closeRoomImageDeleteDialog,
    confirmRemoveRoomImage: visibility.confirmRemoveRoomImage,
    deleteImage: visibility.deleteImage,
    discardRoomImageTitleEdits: edits.discardRoomImageTitleEdits,
    removeRoomImageFromEditor: visibility.removeRoomImageFromEditor,
    removeImageFromRoom: visibility.removeImageFromRoom,
    reorderOfferGalleryImages: order.reorderOfferGalleryImages,
    reorderRoomImages: order.reorderRoomImages,
    saveRoomImageChanges: order.saveRoomImageChanges,
    saveImagesVideoChanges: order.saveImagesVideoChanges,
    discardChanges: order.discardChanges,
    saveRoomImageTitleEdits: edits.saveRoomImageTitleEdits,
    saveImageEdit: edits.saveImageEdit,
    setRoomImagePendingEdit: edits.setRoomImagePendingEdit,
    setVideoId: setLocalVideoId,
    toggleHiddenImage: visibility.toggleHiddenImage,
    updateImageTitle: edits.updateImageTitle,
    uploadFiles: upload.uploadFiles,
    uploadFilesToOfferGallery: upload.uploadFilesToOfferGallery,
    uploadFilesToRoom: upload.uploadFilesToRoom,
    cancelOversizedUpload: upload.cancelOversizedUpload,
    confirmOversizedUpload: upload.confirmOversizedUpload,
    editRoomImage: edits.editRoomImage,
  };
}
